import { FC, ReactNode, useEffect } from 'react';
import useFetchCurrentUser from '@components/features/currentUser/hooks/useFetchCurrentUser';
import { useLocation, useNavigate } from 'react-router-dom';
import Throbber from '@components/primitives/throbber/Throbber';
import { createListPath, PagePath } from '@routing/appUrls';
import log, { LogContext } from '@utils/logging';
import useAuthentication from '@hooks/useAuthentication';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { AuthenticationState } from '@js/store/authenticationAtoms';

interface Props {
    children: ReactNode;
    roles?: Roles[];
}

export enum Roles {
    AllowFileHandler = 'AllowFileHandler',
    AllowRoutingTable = 'AllowRoutingTable',
}

const ProtectedRoute: FC<Props> = ({ children, roles }) => {
    const { isLoading, data: user } = useFetchCurrentUser();
    const location = useLocation();
    const navigate = useNavigate();
    const { authenticationState, getAccessToken } = useAuthentication();
    const client = useQueryClient();
    const { t } = useTranslation();

    const allRoles = [
        {
            id: Roles.AllowFileHandler,
            condition: user?.allowFilehandler,
        },
        {
            id: Roles.AllowRoutingTable,
            condition: user?.allowEditRoutingtable,
        },
    ];

    const isLoggedIn = !!user && !authenticationState && getAccessToken();

    const userHasPermission = (): boolean => {
        let permission = true;
        roles?.forEach((role) => {
            if (!permission) {
                return;
            }

            const roleSettings = allRoles.find((r) => r.id === role);
            if (!roleSettings) {
                console.warn(`Role "${role}" is not supported.`);
                return;
            }

            permission = !!roleSettings.condition;
        });

        return permission;
    };

    const toLoginPage = () => {
        log({
            context: LogContext.User,
            title: 'Redirect to login page',
            reason: 'User is not logged in',
        });

        navigate(PagePath.login, {
            ...(authenticationState !== AuthenticationState.LoggedOut
                ? {
                      state: {
                          from: location.pathname,
                      },
                  }
                : {}),
        });

        if (authenticationState === AuthenticationState.SessionExpired) {
            toast.warn(t('toastSessionExpired', { ns: 'authentication' }));
        } else if (authenticationState === AuthenticationState.LoggedOut) {
            toast.success(t('toastLoggedOut', { ns: 'authentication' }));
        } else if (!user && location.pathname !== '/') {
            toast.warn(t('toastUnauthorized', { ns: 'authentication' }));
        }

        client.removeQueries();
    };

    useEffect(() => {
        if (isLoading) {
            return;
        }

        // authenticationState will be handled in other useEffect
        if (!isLoggedIn && !authenticationState) {
            toLoginPage();
            return;
        }

        // Check roles
        if (!userHasPermission()) {
            log({
                context: LogContext.User,
                title: 'Redirect to list page',
                reason: 'User has no permission',
            });
            navigate(createListPath());
        }
    }, [isLoading, isLoggedIn, roles]);

    useEffect(() => {
        if (!authenticationState) {
            return;
        }

        toLoginPage();
    }, [authenticationState]);

    if (isLoading) {
        return <Throbber />;
    }

    if (!isLoggedIn || !userHasPermission()) {
        return null;
    }

    return <>{children}</>;
};

export default ProtectedRoute;
